import React from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  ComposedChart, Line, AreaChart, Area,
} from "recharts";

/* Brand-driven palette — always starts with the tenant's primary + accent. */
export function palette(brand) {
  const p = brand?.primary || "#003E87";
  const a = brand?.accent || "#F4C400";
  return [p, a, "#2563EB", "#0EA5E9", "#14B8A6", "#64748B", "#F59E0B", "#8B5CF6", "#10B981", "#EC4899", "#94A3B8", "#0F766E"];
}
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const kfmt = (n) => Number(n || 0).toLocaleString("en-US");

export function FigCaption({ n, children }) {
  return <div className="text-[11px] text-slate-500 mt-2 italic">Figure {n}. {children}</div>;
}

/* ---- Fig 1 / pie: proportional share (man-hours) ---- */
export function ProposalPie({ data, brand, height = 240, valueFmt = kfmt }) {
  if (!data?.length) return null;
  const cols = palette(brand);
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  return (
    <div className="flex items-center gap-6 break-inside-avoid">
      <ResponsiveContainer width="45%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="label" outerRadius={height / 2 - 8} strokeWidth={1} stroke="#fff">
            {data.map((_, i) => <Cell key={i} fill={cols[i % cols.length]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [valueFmt(v), n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: cols[i % cols.length] }} />
            <span className="flex-1 text-slate-700 truncate">{d.label}</span>
            <span className="font-mono text-slate-500">{valueFmt(d.value)}</span>
            <span className="font-mono text-slate-400 w-12 text-right">{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Fig 2 / donut: price distribution ---- */
export function ProposalDonut({ data, brand, height = 240, valueFmt = money }) {
  if (!data?.length) return null;
  const cols = palette(brand);
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  return (
    <div className="flex items-center gap-6 break-inside-avoid">
      <ResponsiveContainer width="45%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="label" innerRadius={height / 4} outerRadius={height / 2 - 8} strokeWidth={1} stroke="#fff">
            {data.map((_, i) => <Cell key={i} fill={cols[i % cols.length]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [valueFmt(v), n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: cols[i % cols.length] }} />
            <span className="flex-1 text-slate-700 truncate">{d.label}</span>
            <span className="font-mono text-slate-500">{valueFmt(d.value)}</span>
            <span className="font-mono text-slate-400 w-12 text-right">{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Fig 4 / horizontal bar: sorted values ---- */
export function ProposalBar({ data, brand, height, valueFmt = kfmt, color }) {
  if (!data?.length) return null;
  const cols = palette(brand);
  const h = height || Math.max(data.length * 34 + 20, 120);
  return (
    <ResponsiveContainer width="100%" height={h} className="break-inside-avoid">
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 60, top: 4, bottom: 4 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v) => valueFmt(v)} />
        <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={16}>
          {data.map((_, i) => <Cell key={i} fill={color || cols[i % cols.length]} />)}
          <LabelList dataKey="value" position="right" formatter={valueFmt} style={{ fontSize: 10, fill: "#64748b" }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ---- Fig 5 / stacked area: monthly loaded manpower by phase ---- */
export function ProposalAreaStack({ data, keys, brand, height = 240, label = "" }) {
  if (!data?.length || !keys?.length) return null;
  const cols = palette(brand);
  return (
    <div className="break-inside-avoid">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#94a3b8" }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} label={{ value: label, angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
          <Tooltip />
          {keys.map((k, i) => (
            <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={cols[i % cols.length]} fill={cols[i % cols.length]} fillOpacity={0.75} strokeWidth={1} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <ChartLegend keys={keys} brand={brand} />
    </div>
  );
}

/* ---- Fig 6 / composed: monthly cash bars + billed/collected curves ---- */
export function ProposalCashFlow({ data, brand, height = 260 }) {
  if (!data?.length) return null;
  const cols = palette(brand);
  return (
    <div className="break-inside-avoid">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ left: 6, right: 8, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => "$" + (v / 1e6).toFixed(1) + "M"} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v) => money(v)} />
          <Bar dataKey="net_cash" name="Net cash" fill={cols[0]} radius={[3, 3, 0, 0]} barSize={16} />
          <Line type="monotone" dataKey="billing" name="Billed" stroke={cols[1]} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="collected" name="Collected" stroke="#10B981" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend keys={["Net cash", "Billed", "Collected"]} colors={[cols[0], cols[1], "#10B981"]} />
    </div>
  );
}

/* ---- Fig 9 / stacked bar: equipment deployment by phase (monthly) ---- */
export function ProposalStackedBar({ data, keys, brand, height = 240, label = "" }) {
  if (!data?.length || !keys?.length) return null;
  const cols = palette(brand);
  return (
    <div className="break-inside-avoid">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#94a3b8" }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} label={{ value: label, angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
          <Tooltip />
          {keys.map((k, i) => <Bar key={k} dataKey={k} stackId="1" fill={cols[i % cols.length]} barSize={16} />)}
        </BarChart>
      </ResponsiveContainer>
      <ChartLegend keys={keys} brand={brand} />
    </div>
  );
}

function ChartLegend({ keys, brand, colors }) {
  const cols = colors || palette(brand);
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500 pt-1.5 justify-center">
      {keys.map((k, i) => (
        <span key={k} className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: cols[i % cols.length] }} />{k}
        </span>
      ))}
    </div>
  );
}

/* ---- Fig 3 / Gantt: execution timeline by activity ---- */
export function ProposalGantt({ tasks, projectEnd, startDate, brand, testid }) {
  if (!tasks?.length) return null;
  const pe = projectEnd || 1;
  const cols = palette(brand);
  const laneColor = (t, i) => {
    const d = (t.discipline || "").toLowerCase();
    if (d === "electrical") return cols[1];
    if (d === "mechanical") return cols[0];
    return cols[(i + 2) % cols.length];
  };
  const start = startDate ? new Date(startDate) : null;
  const dayLabel = (dnum) => {
    if (!start) return `D${Math.round(dnum)}`;
    const dt = new Date(start.getTime() + dnum * 86400000);
    return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  };
  return (
    <div className="space-y-1 break-inside-avoid" data-testid={testid}>
      {tasks.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-40 shrink-0 text-[11px] text-slate-600 truncate">{t.name}</div>
          <div className="flex-1 relative h-4 bg-slate-100 rounded">
            <div className="absolute h-4 rounded" style={{
              left: `${(t.start / pe) * 100}%`,
              width: `${Math.max(((t.finish - t.start) / pe) * 100, 1.5)}%`,
              backgroundColor: laneColor(t, i),
              outline: t.critical ? "1.5px solid #E11D48" : "none", outlineOffset: "-1px",
            }} />
          </div>
          <div className="w-14 shrink-0 text-right text-[10px] text-slate-400 font-mono">{t.days}d</div>
        </div>
      ))}
      <div className="flex justify-between text-[9px] text-slate-400 pl-40 pr-16 pt-1">
        <span>{dayLabel(0)}</span><span>{dayLabel(pe / 2)}</span><span>{dayLabel(pe)}</span>
      </div>
      <div className="flex gap-3 text-[10px] text-slate-400 pt-1 justify-center">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: cols[0] }} />Mechanical</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: cols[1] }} />Electrical</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm border-2 border-rose-500" />Critical path</span>
      </div>
    </div>
  );
}

/* ---- Fig 11 / seasonal weather calendar ---- */
export function WeatherCalendar({ data }) {
  if (!data?.length) return null;
  // Color encodes the dominant limiting driver; height/opacity encodes impact (days lost). W7.
  const COND = {
    Cold: { bg: "#DBEAFE", bar: "#2563EB", fg: "#1D4ED8", label: "Cold / winter" },
    Heat: { bg: "#FEE2E2", bar: "#DC2626", fg: "#B91C1C", label: "Extreme heat" },
    Wet: { bg: "#CCFBF1", bar: "#0D9488", fg: "#0F766E", label: "Wet / rain" },
    Wind: { bg: "#E2E8F0", bar: "#64748B", fg: "#475569", label: "High wind" },
    Mild: { bg: "#FEF9C3", bar: "#CA8A04", fg: "#A16207", label: "Mild / normal" },
    Normal: { bg: "#FEF9C3", bar: "#CA8A04", fg: "#A16207", label: "Mild / normal" },
  };
  const mx = Math.max(...data.map((m) => m.days_lost || 0), 1);
  const legend = ["Cold", "Heat", "Wet", "Wind", "Mild"];
  return (
    <div data-testid="proposal-weather-calendar">
      <div className="grid grid-cols-6 gap-1.5 break-inside-avoid">
        {data.map((m, i) => {
          const c = COND[m.condition] || COND.Mild;
          const pct = Math.min(1, (m.days_lost || 0) / mx);
          return (
            <div key={i} className="rounded p-2 text-center border border-slate-100" style={{ background: c.bg }}
              data-testid={`weather-cell-${m.month}`} title={m.driver ? `${m.month}: ${m.driver} · ${m.days_lost} days lost` : `${m.month}: mild`}>
              <div className="text-[10px] font-semibold" style={{ color: c.fg }}>{m.month}</div>
              <div className="text-lg font-bold leading-none" style={{ color: c.fg }}>{m.days_lost}</div>
              <div className="text-[9px] text-slate-500">days lost</div>
              <div className="h-1.5 rounded-full bg-white/70 mt-1 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(pct * 100, m.days_lost ? 8 : 0)}%`, background: c.bar }} />
              </div>
              <div className="text-[9px] mt-0.5 font-medium" style={{ color: c.fg }}>{c.label.split(" / ")[0]} · {m.risk}</div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2" data-testid="weather-legend">
        {legend.map((k) => (
          <span key={k} className="flex items-center gap-1 text-[9px] text-slate-500">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COND[k].bar }} /> {COND[k].label}
          </span>
        ))}
        <span className="text-[9px] text-slate-400">· bar length = field-days lost</span>
      </div>
    </div>
  );
}
